import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';

import type { BusinessProcessResponse, CreateBusinessProcessParams } from '@/domain/models/project.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { gradientButtonSx } from '@/shared/utils/brand-gradient.js';

interface Props {
    open: boolean;
    mode: 'add' | 'edit';
    data?: BusinessProcessResponse;
    onClose: () => void;
    onConfirm: (params: CreateBusinessProcessParams) => Promise<boolean>;
}

export function BusinessProcessDialog({ open, mode, data, onClose, onConfirm }: Props) {
    const { translate } = useApp();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(data ? data.name : '');
            setDescription(data ? (data.description ?? '') : '');
        }
    }, [open, data]);

    const handleConfirm = async () => {
        if (!name.trim()) {
            return;
        }
        setSaving(true);
        const params = { name: name.trim(), description: description.trim() || null };
        const ok = await onConfirm(params);
        setSaving(false);
        if (ok) {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="business-process-dialog">
            <DialogTitle>{mode === 'add' ? translate('projects.addBusinessProcess') : translate('common.edit')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label={`${translate('projects.processName')} *`}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: 200 }}
                    />
                    <TextField
                        label={translate('projects.processDescription')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
                    {translate('common.cancel')}
                </Button>
                <Button onClick={handleConfirm} variant="contained" disabled={saving} disableElevation sx={gradientButtonSx}>
                    {translate('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
